import React, { useState } from 'react';
import { useConversation } from '../../context/ConversationProvider';
import { useMessages } from '../../context/MessageProvider';
import { usePresence } from '../../context/UserPresenceProvider';
import { useUIState } from '../../context/UIStateProvider';
import './ForwardModal.css';

const ForwardModal = () => {
    // Use modular contexts instead of legacy useChat
    const {
        conversations,
        currentUser,
        setActiveConversationId,
    } = useConversation();
    const { sendMessage } = useMessages();
    const { presenceByUserId } = usePresence();
    const { forwardingMessage, setForwardingMessage } = useUIState();

    const [selectedConversations, setSelectedConversations] = useState([]);
    const [isForwarding, setIsForwarding] = useState(false);

    if (!forwardingMessage) return null;

    // Filter out conversations to show (exclude current active one if needed)
    const availableConversations = (conversations || []).filter(conv => {
        // Get display name for filtering
        return conv.id && conv.memberIds?.length > 0;
    });

    const getConversationName = (conv) => {
        if (conv.type === 'group') {
            return conv.title || 'Group Chat';
        }
        // For direct chats, find the other user and get name from presence data
        const otherMemberId = conv.memberIds?.find(id => id !== currentUser?.uid);
        if (otherMemberId && presenceByUserId?.[otherMemberId]) {
            const presence = presenceByUserId[otherMemberId];
            return presence.displayName || presence.username || 'User';
        }
        return 'User';
    };

    const handleToggleConversation = (convId) => {
        setSelectedConversations(prev =>
            prev.includes(convId)
                ? prev.filter(id => id !== convId)
                : [...prev, convId]
        );
    };

    const handleForward = async () => {
        if (selectedConversations.length === 0 || isForwarding) return;

        setIsForwarding(true);

        // FIX 2.2: Track partial success/failure instead of all-or-nothing
        const results = { succeeded: [], failed: [] };

        for (const convId of selectedConversations) {
            try {
                const forwardedContent = forwardingMessage.content
                    ? `↪️ Forwarded:\n${forwardingMessage.content}`
                    : '↪️ Forwarded message';

                await sendMessage({
                    conversationId: convId,
                    content: forwardedContent,
                    imageUrl: forwardingMessage.imageUrl || null,
                    fileUrl: forwardingMessage.fileUrl || null,
                    fileName: forwardingMessage.fileName || null,
                    sharedPost: forwardingMessage.sharedPost || null,
                });
                results.succeeded.push(convId);
            } catch (error) {
                results.failed.push(convId);
                console.error(`[ForwardModal] Forward failed for ${convId}:`, error);
            }
        }

        if (results.failed.length > 0) {
            // FIX M-7: Use console.warn instead of blocking alert()
            // TODO: Integrate with app's toast notification system
            console.warn(`[ForwardModal] Sent to ${results.succeeded.length}/${selectedConversations.length} conversations. ${results.failed.length} failed.`);
        }

        if (results.succeeded.length > 0) {
            // Close modal and navigate if all or some succeeded
            setForwardingMessage(null);
            if (results.succeeded.length === 1) {
                setActiveConversationId(results.succeeded[0]);
            }
        }

        setIsForwarding(false);
    };

    const handleClose = () => {
        setForwardingMessage(null);
        setSelectedConversations([]);
    };

    return (
        <div className="forward-modal-overlay" onClick={handleClose}>
            <div className="forward-modal" onClick={e => e.stopPropagation()}>
                <div className="forward-modal__header">
                    <h3>Forward Message</h3>
                    <button
                        className="forward-modal__close"
                        onClick={handleClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="forward-modal__preview">
                    <div className="forward-modal__preview-label">Message to forward:</div>
                    <div className="forward-modal__preview-content">
                        {forwardingMessage.content?.substring(0, 100) || '[Media]'}
                        {forwardingMessage.content?.length > 100 ? '...' : ''}
                    </div>
                </div>

                <div className="forward-modal__conversations">
                    <div className="forward-modal__section-label">
                        Select conversation{selectedConversations.length > 0 ? ` (${selectedConversations.length})` : ''}:
                    </div>
                    <div className="forward-modal__list">
                        {availableConversations.length === 0 ? (
                            <div className="forward-modal__empty">No conversations available</div>
                        ) : (
                            availableConversations.map(conv => (
                                <button
                                    key={conv.id}
                                    type="button"
                                    className={`forward-modal__item ${selectedConversations.includes(conv.id) ? 'forward-modal__item--selected' : ''}`}
                                    onClick={() => handleToggleConversation(conv.id)}
                                >
                                    <div className="forward-modal__item-avatar">
                                        {conv.type === 'group' ? '👥' : getConversationName(conv)[0]?.toUpperCase() || '?'}
                                    </div>
                                    <div className="forward-modal__item-name">
                                        {getConversationName(conv)}
                                    </div>
                                    {selectedConversations.includes(conv.id) && (
                                        <div className="forward-modal__item-check">✓</div>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>

                <div className="forward-modal__footer">
                    <button
                        className="forward-modal__cancel"
                        onClick={handleClose}
                        disabled={isForwarding}
                    >
                        Cancel
                    </button>
                    <button
                        className="forward-modal__send"
                        onClick={handleForward}
                        disabled={selectedConversations.length === 0 || isForwarding}
                    >
                        {isForwarding ? 'Forwarding...' : `Forward${selectedConversations.length > 1 ? ` to ${selectedConversations.length}` : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForwardModal;
